import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import {
  CreateAssignmentModalController,
  CreateAssignmentModalControllerDelegate,
  IUser,
  UserEntry,
} from "../CreateAssignmentModal/controller";
import { IAssignment } from "../controller";
import { environment } from "../../utils/environment";

export interface AllocateRespondentsModalDelegate {
  shouldCloseModal(): void;
  applicationShouldLogout(): void;
  didAllocateRespondents(): void;
}

interface Props {
  assignment: IAssignment;
  delegate: AllocateRespondentsModalDelegate;
}

interface State {
  submissionPhase: "idle" | "inviting" | "done";
  inviteLog: { email: string; name: string; done: boolean; error?: string }[];
  usersInput: string;
  usersSearchResults: IUser[];
  usersLoading: boolean;
  usersToAssign: UserEntry[];
  activeUserTab: number;
  newUserFirstName: string;
  newUserLastName: string;
  newUserEmail: string;
  newUserRecaptchaToken: string;
}

class AllocateRespondentsModal
  extends React.Component<Props, State>
  implements CreateAssignmentModalControllerDelegate {

  private controller: CreateAssignmentModalController;
  private usersFetchDebounce?: NodeJS.Timeout;
  private closeTimer?: NodeJS.Timeout;
  private recaptchaRef = React.createRef<ReCAPTCHA>();

  constructor(props: Props) {
    super(props);
    this.state = {
      submissionPhase: "idle",
      inviteLog: [],
      usersInput: "",
      usersSearchResults: [],
      usersLoading: false,
      usersToAssign: [],
      activeUserTab: 0,
      newUserFirstName: "",
      newUserLastName: "",
      newUserEmail: "",
      newUserRecaptchaToken: "",
    };
    this.controller = new CreateAssignmentModalController(this);
  }

  // CreateAssignmentModalControllerDelegate — no-ops for unused methods
  didGetInstruments() {}
  didCreateAssignment() {}

  didGetUsers(users: IUser[]) {
    this.setState({ usersSearchResults: users, usersLoading: false });
  }

  applicationShouldLogout() {
    this.props.delegate.applicationShouldLogout();
  }

  genericError() {}

  componentWillUnmount() {
    clearTimeout(this.usersFetchDebounce!);
    clearTimeout(this.closeTimer!);
  }

  private searchUsers(filter: string) {
    this.setState({ usersLoading: true });
    clearTimeout(this.usersFetchDebounce!);
    this.usersFetchDebounce = setTimeout(() => {
      const token = localStorage.getItem("AccessToken")!;
      this.controller.searchUsers(token, filter.trim() || undefined);
    }, 300);
  }

  private addExistingUserToQueue(user: IUser) {
    const { usersToAssign } = this.state;
    const maxUsers = this.props.assignment.totalApplicationCount;
    if (usersToAssign.length >= maxUsers) return;
    if (usersToAssign.some((u) => u.id === user.uid || u.email === user.email)) return;
    this.setState((prev) => ({
      usersToAssign: [
        ...prev.usersToAssign,
        {
          id: user.uid,
          type: "existing",
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
        },
      ],
      usersInput: "",
      usersSearchResults: [],
    }));
  }

  private addNewUserToQueue() {
    const {
      newUserFirstName,
      newUserLastName,
      newUserEmail,
      newUserRecaptchaToken,
      usersToAssign,
    } = this.state;
    const maxUsers = this.props.assignment.totalApplicationCount;
    if (!newUserFirstName || !newUserLastName || !newUserEmail || !newUserRecaptchaToken) return;
    if (usersToAssign.length >= maxUsers) return;
    if (usersToAssign.some((u) => u.email === newUserEmail)) return;
    this.setState(
      (prev) => ({
        usersToAssign: [
          ...prev.usersToAssign,
          {
            id: `new-${Date.now()}`,
            type: "new",
            firstName: newUserFirstName,
            lastName: newUserLastName,
            email: newUserEmail,
            recaptchaToken: newUserRecaptchaToken,
          },
        ],
        newUserFirstName: "",
        newUserLastName: "",
        newUserEmail: "",
        newUserRecaptchaToken: "",
      }),
      () => {
        this.recaptchaRef.current?.reset();
      }
    );
  }

  private removeFromQueue(id: string) {
    this.setState((prev) => ({
      usersToAssign: prev.usersToAssign.filter((u) => u.id !== id),
    }));
  }

  private async handleAllocate() {
    const { usersToAssign } = this.state;
    const { assignment } = this.props;
    if (usersToAssign.length === 0) return;

    const token = localStorage.getItem("AccessToken")!;
    this.setState({
      submissionPhase: "inviting",
      inviteLog: usersToAssign.map((u) => ({
        email: u.email,
        name: `${u.firstName} ${u.lastName}`,
        done: false,
      })),
    });

    let hadErrors = false;
    for (let i = 0; i < usersToAssign.length; i++) {
      try {
        await this.controller.assignSingleUser(token, usersToAssign[i], assignment.applicationId);
        this.setState((prev) => {
          const log = [...prev.inviteLog];
          log[i] = { ...log[i], done: true };
          return { inviteLog: log };
        });
      } catch (err: any) {
        hadErrors = true;
        const error = err?.response?.data?.message || "Erro ao associar respondente";
        this.setState((prev) => {
          const log = [...prev.inviteLog];
          log[i] = { ...log[i], done: true, error };
          return { inviteLog: log };
        });
      }
    }

    this.setState({ submissionPhase: "done" });
    if (!hadErrors) {
      this.closeTimer = setTimeout(() => {
        this.props.delegate.didAllocateRespondents();
      }, 3000);
    }
  }

  renderUserPanel() {
    const {
      usersSearchResults,
      usersInput,
      usersLoading,
      usersToAssign,
      activeUserTab,
      newUserFirstName,
      newUserLastName,
      newUserEmail,
    } = this.state;
    const maxUsers = this.props.assignment.totalApplicationCount;
    const isFull = usersToAssign.length >= maxUsers;

    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color={isFull ? "error.main" : "text.secondary"}>
            {usersToAssign.length} de {maxUsers} licença{maxUsers !== 1 ? "s" : ""} disponíve{maxUsers !== 1 ? "is" : "l"}
          </Typography>
          {isFull && (
            <Typography variant="caption" color="error.main" fontWeight="bold">
              Limite atingido
            </Typography>
          )}
        </Box>

        <Tabs
          value={activeUserTab}
          onChange={(_, val) => this.setState({ activeUserTab: val })}
          variant="fullWidth"
          sx={{
            bgcolor: "grey.200",
            borderRadius: 1,
            "& .MuiTab-root": { borderRadius: 1 },
          }}
        >
          <Tab label="Meus respondentes" sx={{ textTransform: "none" }} />
          <Tab label="Novo respondente" sx={{ textTransform: "none" }} />
        </Tabs>

        {activeUserTab === 0 && (
          <Autocomplete
            disabled={isFull}
            options={usersSearchResults}
            getOptionLabel={(user: IUser) =>
              `${user.firstName} ${user.lastName} (${user.email})`
            }
            value={null}
            inputValue={usersInput}
            onInputChange={(_, value, reason) => {
              if (reason === "input") {
                this.setState({ usersInput: value }, () => this.searchUsers(value));
              } else if (reason === "clear") {
                this.setState({ usersInput: "" });
              }
            }}
            onChange={(_, user: IUser | null) => {
              if (user) this.addExistingUserToQueue(user);
            }}
            loading={usersLoading}
            filterOptions={(x) => x}
            isOptionEqualToValue={(a, b) => a.uid === b.uid}
            noOptionsText={
              usersInput.length === 0
                ? "Digite para buscar respondentes"
                : "Nenhum respondente encontrado"
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar respondente"
                helperText={
                  isFull
                    ? "Limite de licenças atingido"
                    : "Selecione para adicionar à lista abaixo"
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            ListboxProps={{ style: { maxHeight: 200 } }}
          />
        )}

        {activeUserTab === 1 && (
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Typography variant="caption" color="text.secondary">
              Um convite será enviado por e-mail ao alocar.
            </Typography>
            <TextField
              label="Nome"
              required
              disabled={isFull}
              value={newUserFirstName}
              onChange={(e) => this.setState({ newUserFirstName: e.target.value })}
            />
            <TextField
              label="Sobrenome"
              required
              disabled={isFull}
              value={newUserLastName}
              onChange={(e) => this.setState({ newUserLastName: e.target.value })}
            />
            <TextField
              label="E-mail"
              required
              disabled={isFull}
              value={newUserEmail}
              onChange={(e) =>
                this.setState({ newUserEmail: e.target.value.toLowerCase().trim() })
              }
            />
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <ReCAPTCHA
                ref={this.recaptchaRef}
                sitekey={environment.google.recaptchaToken}
                onChange={(token) => this.setState({ newUserRecaptchaToken: token ?? "" })}
                onExpired={() => this.setState({ newUserRecaptchaToken: "" })}
              />
            </Box>
            <Button
              variant="contained"
              disabled={
                isFull ||
                !newUserFirstName ||
                !newUserLastName ||
                !newUserEmail ||
                !this.state.newUserRecaptchaToken
              }
              onClick={() => this.addNewUserToQueue()}
              sx={{ alignSelf: "flex-end" }}
            >
              Adicionar à lista
            </Button>
          </Box>
        )}

        <Divider />

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            Serão atribuídos ao alocar:
          </Typography>
          {usersToAssign.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
              Nenhum respondente adicionado ainda.
            </Typography>
          ) : (
            <Stack direction="column" gap={1}>
              {usersToAssign.map((user) => (
                <Chip
                  key={user.id}
                  label={
                    user.type === "new"
                      ? `${user.firstName} ${user.lastName} — convite (${user.email})`
                      : `${user.firstName} ${user.lastName} (${user.email})`
                  }
                  onDelete={() => this.removeFromQueue(user.id)}
                  variant="outlined"
                  color={user.type === "new" ? "primary" : "default"}
                  sx={{
                    height: "auto",
                    width: "100%",
                    justifyContent: "space-between",
                    "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 },
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    );
  }

  renderInviteLog() {
    const { inviteLog, submissionPhase } = this.state;
    const allDone = inviteLog.every((e) => e.done);
    const hasErrors = inviteLog.some((e) => e.error);

    const title =
      allDone && submissionPhase === "done"
        ? "Respondentes associados!"
        : "Associando respondentes...";

    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
          {title}
        </Typography>
        <Stack direction="column" gap={1.5}>
          {inviteLog.map((entry, i) => (
            <Box key={i} display="flex" flexDirection="column" gap={0.25}>
              <Box display="flex" alignItems="center" gap={1.5}>
                {!entry.done ? (
                  <CircularProgress size={18} sx={{ flexShrink: 0 }} />
                ) : entry.error ? (
                  <CancelIcon sx={{ color: "error.main", fontSize: 20, flexShrink: 0 }} />
                ) : (
                  <CheckCircleIcon sx={{ color: "success.main", fontSize: 20, flexShrink: 0 }} />
                )}
                <Typography variant="body2">
                  {entry.name}{" "}
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({entry.email})
                  </Typography>
                </Typography>
              </Box>
              {entry.error && (
                <Typography variant="caption" color="error.main" sx={{ pl: "33.5px" }}>
                  {entry.error}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
        {hasErrors && submissionPhase === "done" && (
          <Button
            variant="outlined"
            onClick={() => this.props.delegate.shouldCloseModal()}
            sx={{ alignSelf: "flex-end", mt: 1 }}
          >
            Fechar
          </Button>
        )}
      </Box>
    );
  }

  render() {
    const { submissionPhase, usersToAssign } = this.state;
    const { assignment } = this.props;
    const isIdle = submissionPhase === "idle";
    const isSubmitting = submissionPhase === "inviting" || submissionPhase === "done";

    return (
      <Dialog
        open
        onClose={() => isIdle && this.props.delegate.shouldCloseModal()}
        fullWidth
        maxWidth="sm"
        variant="modal"
      >
        <DialogTitle variant="modal">
          <GroupAddIcon />
          Alocar respondentes
        </DialogTitle>
        <DialogContent variant="modal">
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Typography variant="body2" color="text.secondary">
              Aplicação: <strong>{assignment.title}</strong>
            </Typography>
            {isSubmitting ? this.renderInviteLog() : this.renderUserPanel()}
          </Box>
        </DialogContent>
        {isIdle && (
          <DialogActions variant="modal" sx={{ gap: 1 }}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => this.props.delegate.shouldCloseModal()}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={usersToAssign.length === 0}
              onClick={() => this.handleAllocate()}
            >
              Alocar respondentes
            </Button>
          </DialogActions>
        )}
      </Dialog>
    );
  }
}

export default AllocateRespondentsModal;
